/**
 * Share settings admin API — PMA-ADMIN-014 (EPIC-27).
 *
 * The same document backs the public payload's `share_visibility`
 * (PMA-PUB-014, backend-owned). All calls go through the shared apiClient
 * (axios + Firebase ID-token interceptor). Never import this from a
 * non-admin page — the routes are guarded by ProtectedRoute
 * allowedRoles=["admin"] in App.tsx.
 */
import { apiClient } from "./api";
import {
  DEFAULT_SHARE_VISIBILITY,
  type ShareSectionMap,
  type ShareUpsell,
} from "./shareVisibility";

export interface ShareSettingsPutBody {
  sections: ShareSectionMap;
  upsell: ShareUpsell;
}

export interface ShareSettings extends ShareSettingsPutBody {
  updated_at?: string | null;
  updated_by?: string | null;
}

/**
 * GET /admin/share-settings
 * Falls back to the client-side defaults (all sections visible, placeholder
 * upsell copy) when the backend returns 404 (no document saved yet).
 */
export async function getShareSettings(): Promise<ShareSettings> {
  try {
    return await apiClient.get<ShareSettings>("/admin/share-settings");
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) {
      return {
        sections: { ...DEFAULT_SHARE_VISIBILITY.sections },
        upsell: { ...DEFAULT_SHARE_VISIBILITY.upsell },
      };
    }
    throw err;
  }
}

/**
 * PUT /admin/share-settings
 * Full replace — 13 section keys; `top_players` and `player_card` are optional on PUT (default
 * to visible), every other key is required. Returns the stored document.
 */
export async function putShareSettings(
  body: ShareSettingsPutBody,
): Promise<ShareSettings> {
  return apiClient.put<ShareSettings>("/admin/share-settings", body);
}
